import { useEffect, useState } from "react";

import { Loader2, Plus, Save } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { makeRequest } from "@/utils/makeRequest";

import { toast } from "sonner";

import type { BlockchainNetwork } from "@/interfaces/blockchainNetwork";
import type { BlockchainNetworkFormValues } from "./BlockchainNetworkForm";
import BlockchainNetworkForm from "./BlockchainNetworkForm";
import service from "./service";

interface Props {
  open: boolean;

  mode: "create" | "edit";

  network?: BlockchainNetwork | null;

  onClose: () => void;

  onSuccess?: () => void;
}

const BlockchainNetworkDialog = ({
  open,
  mode,
  network,
  onClose,
  onSuccess,
}: Props) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setLoading(false);
    }
  }, [open]);

  const handleSubmit = async (values: BlockchainNetworkFormValues) => {
    try {
      setLoading(true);

      const payload = {
        ...values,

        chainId: values.chainId || null,

        explorerUrl: values.explorerUrl || null,

        rpcUrl: values.rpcUrl || null,

        wsUrl: values.wsUrl || null,
      };

      if (mode === "create") {
        await service.create(payload);

        toast.success("Blockchain network created successfully.");
      } else {
        await service.update(network!.id, payload);

        toast.success("Blockchain network updated successfully.");
      }

      await onSuccess?.();

      onClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ?? "Unable to save blockchain network.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === "create" ? (
              <>
                <Plus className="h-5 w-5" />
                Create Blockchain Network
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                Edit Blockchain Network
              </>
            )}
          </DialogTitle>

          <DialogDescription>
            {mode === "create"
              ? "Configure a new blockchain network for deposits, withdrawals and asset management."
              : "Update blockchain network settings."}
          </DialogDescription>
        </DialogHeader>

        <BlockchainNetworkForm
          loading={loading}
          initialValues={network ?? undefined}
          onSubmit={handleSubmit}
        />
      </DialogContent>
    </Dialog>
  );
};

export default BlockchainNetworkDialog;
